import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Database, Clock, UserCheck, Server, Brain, Globe, Lock, Bell, Cookie, FileEdit, ExternalLink } from 'lucide-react';

/**
 * Page Politique de Confidentialité (RGPD)
 * Conforme au RGPD et à la Loi Informatique & Libertés - Version complète
 * Basé sur le document juridique Hippocrate_MG_Politique_Confidentialite.docx
 */
export const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-slate-950 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft size={18} />
            Retour
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2">Politique de Confidentialité</h1>
          <p className="text-slate-400">Protection des données à caractère personnel — RGPD</p>
          <div className="flex gap-4 mt-4 text-sm text-slate-500">
            <span>Service « Hippocrate MG »</span>
            <span>•</span>
            <span>Version : 1.0</span>
            <span>•</span>
            <span>Dernière mise à jour : Décembre 2025</span>
          </div>
        </div>

        {/* Préambule */}
        <div className="bg-blue-900/30 border border-blue-700 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-3">
            <Shield className="text-blue-400 flex-shrink-0 mt-1" size={24} />
            <div>
              <h2 className="text-lg font-bold text-blue-300 mb-3">Préambule</h2>
              <p className="text-blue-200 leading-relaxed">
                La présente Politique de Confidentialité décrit la manière dont les données à caractère personnel sont collectées, traitées et protégées dans le cadre de l'utilisation du service « Hippocrate MG » (ci-après le « Service »). Elle s'inscrit dans le respect du <strong>Règlement (UE) 2016/679 (« RGPD »)</strong> et de la <strong>loi n° 78-17 du 6 janvier 1978 modifiée (« Loi Informatique et Libertés »)</strong>.
              </p>
              <p className="text-blue-200 leading-relaxed mt-2">
                Elle complète les <Link to="/mentions-legales" className="text-blue-400 hover:underline">Conditions Générales d'Utilisation</Link> du Service, dont elle fait partie intégrante.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-slate max-w-none space-y-6">
          
          {/* Article 1 - Responsable du traitement */}
          <section className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <UserCheck className="text-emerald-400" size={20} />
              <h3 className="text-xl font-semibold text-white">Article 1 — Responsable du traitement et délégué à la protection des données</h3>
            </div>
            <div className="text-slate-300 leading-relaxed space-y-4">
              <div>
                <h4 className="text-emerald-300 font-semibold mb-2">1.1 — Responsable du traitement</h4>
                <p>Le responsable du traitement est :</p>
                <ul className="list-none space-y-1 pl-4 mt-2">
                  <li><strong className="text-white">Dr STEPANIAN Tigran</strong>, médecin</li>
                  <li>N° RPPS : 10101555802</li>
                </ul>
              </div>
              <div>
                <h4 className="text-emerald-300 font-semibold mb-2">1.2 — Contact</h4>
                <p>Pour toute question relative à la présente politique : <a href="mailto:dr.stepanian.tigran@gmail.com" className="text-blue-400 hover:underline">dr.stepanian.tigran@gmail.com</a></p>
              </div>
              <div>
                <h4 className="text-emerald-300 font-semibold mb-2">1.3 — Délégué à la protection des données (DPO)</h4>
                <p>Pour les questions relatives à vos données personnelles, vous pouvez contacter : <a href="mailto:dr.stepanian.tigran@gmail.com" className="text-blue-400 hover:underline">dr.stepanian.tigran@gmail.com</a></p>
              </div>
            </div>
          </section>

          {/* Article 2 - Qualification des parties */}
          <section className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Article 2 — Qualification des parties</h3>
            <div className="text-slate-300 leading-relaxed">
              <p className="mb-4">Selon les traitements concernés, l'Éditeur peut agir :</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong className="text-white">en qualité de responsable de traitement</strong>, pour les traitements liés à la gestion des comptes, à la relation avec l'Utilisateur et au fonctionnement du Service ;</li>
                <li>le cas échéant, <strong className="text-white">en qualité de sous-traitant de l'Utilisateur</strong> (art. 28 RGPD), lorsque ce dernier détermine les finalités et moyens d'un traitement qu'il met en œuvre via le Service ; un contrat de sous-traitance distinct encadre alors ces opérations.</li>
              </ul>
            </div>
          </section>

          {/* Article 3 - Données collectées */}
          <section className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Database className="text-blue-400" size={20} />
              <h3 className="text-xl font-semibold text-white">Article 3 — Données collectées et finalités</h3>
            </div>
            <div className="text-slate-300 leading-relaxed">
              <p className="mb-4">Les catégories de données traitées, leurs finalités et leurs bases légales sont les suivantes :</p>
              
              {/* Tableau des données */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-slate-700 rounded-lg overflow-hidden">
                  <thead className="bg-slate-800">
                    <tr>
                      <th className="text-left p-3 text-white border-b border-slate-700">Catégorie de données</th>
                      <th className="text-left p-3 text-white border-b border-slate-700">Finalité</th>
                      <th className="text-left p-3 text-white border-b border-slate-700">Base légale</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    <tr className="bg-slate-900/50">
                      <td className="p-3">Données d'identification (email, nom)</td>
                      <td className="p-3">Création et gestion du compte</td>
                      <td className="p-3 text-emerald-300">Exécution du contrat</td>
                    </tr>
                    <tr className="bg-slate-900/30">
                      <td className="p-3">Données de connexion (IP, horodatage)</td>
                      <td className="p-3">Sécurité et fonctionnement</td>
                      <td className="p-3 text-blue-300">Intérêt légitime</td>
                    </tr>
                    <tr className="bg-slate-900/50">
                      <td className="p-3">Historique des recherches</td>
                      <td className="p-3">Fourniture du service documentaire</td>
                      <td className="p-3 text-emerald-300">Exécution du contrat</td>
                    </tr>
                    <tr className="bg-slate-900/30">
                      <td className="p-3">Données de navigation (cookies)</td>
                      <td className="p-3">Mesure d'audience (si consentement)</td>
                      <td className="p-3 text-amber-300">Consentement</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-emerald-900/30 border border-emerald-700/50 rounded-lg p-4 mt-4">
                <p className="text-emerald-200 text-sm">
                  <strong>IMPORTANT :</strong> Aucune donnée de santé identifiante relative à un patient n'est demandée ni stockée par le Service. Le Service est un outil documentaire et non un logiciel de gestion de dossiers patients.
                </p>
              </div>
            </div>
          </section>

          {/* Article 4 - Caractère obligatoire */}
          <section className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Article 4 — Caractère obligatoire ou facultatif des données</h3>
            <p className="text-slate-300 leading-relaxed">
              Les données marquées comme obligatoires lors de la collecte sont nécessaires à la fourniture du Service ; à défaut, l'accès ou certaines fonctionnalités ne pourront être assurés. Les autres données sont facultatives.
            </p>
          </section>

          {/* Article 5 - Destinataires */}
          <section className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Server className="text-purple-400" size={20} />
              <h3 className="text-xl font-semibold text-white">Article 5 — Destinataires et sous-traitants</h3>
            </div>
            <div className="text-slate-300 leading-relaxed space-y-4">
              <p>Les données sont destinées aux services habilités de l'Éditeur. Elles peuvent être communiquées à des sous-traitants intervenant pour le compte de l'Éditeur, notamment :</p>
              <ul className="list-disc list-inside space-y-2">
                <li>l'hébergeur du Service ;</li>
                <li>le ou les prestataires d'infrastructure et de fournisseur(s) de modèles d'intelligence artificielle ;</li>
                <li>les prestataires de paiement et de facturation (le cas échéant) ;</li>
                <li>les outils de support, de mesure d'audience ou de communication (le cas échéant).</li>
              </ul>
              <div className="bg-slate-800/50 rounded-lg p-4 mt-4">
                <p className="text-slate-400 text-sm">
                  Chaque sous-traitant est lié par un contrat conforme à l'article 28 du RGPD. <strong className="text-white">Aucune donnée n'est vendue ni cédée à des tiers à des fins de prospection commerciale sans consentement.</strong>
                </p>
              </div>
            </div>
          </section>

          {/* Article 6 - IA */}
          <section className="bg-slate-900/50 border border-purple-700 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Brain className="text-purple-400" size={20} />
              <h3 className="text-xl font-semibold text-white">Article 6 — Traitement par des systèmes d'intelligence artificielle</h3>
            </div>
            <div className="text-slate-300 leading-relaxed space-y-4">
              <p>Le Service repose, en tout ou partie, sur des systèmes d'intelligence artificielle. Les requêtes peuvent être transmises au(x) fournisseur(s) de ces systèmes aux seules fins de génération du Contenu.</p>
              <p>L'Éditeur veille, par des stipulations contractuelles, à ce que ces données ne soient pas réutilisées pour l'entraînement des modèles, sauf information et base légale spécifiques.</p>
              <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-4">
                <p className="text-blue-200 text-sm">
                  <strong>Article 22 RGPD :</strong> Aucune décision produisant des effets juridiques ou affectant de manière significative une personne n'est prise sur le seul fondement d'un traitement automatisé : le Service constitue une aide documentaire dont l'interprétation relève du professionnel de santé.
                </p>
              </div>
            </div>
          </section>

          {/* Article 7 - Transferts hors UE */}
          <section className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Globe className="text-blue-400" size={20} />
              <h3 className="text-xl font-semibold text-white">Article 7 — Transferts hors de l'Union européenne</h3>
            </div>
            <p className="text-slate-300 leading-relaxed">
              Les données sont en principe hébergées et traitées au sein de l'Union européenne. En cas de transfert vers un pays tiers, celui-ci n'intervient que vers un pays bénéficiant d'une <strong className="text-white">décision d'adéquation</strong> ou sur le fondement de <strong className="text-white">garanties appropriées</strong> (notamment clauses contractuelles types de la Commission européenne, art. 46 RGPD).
            </p>
          </section>

          {/* Article 8 - Durées de conservation */}
          <section className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="text-amber-400" size={20} />
              <h3 className="text-xl font-semibold text-white">Article 8 — Durées de conservation</h3>
            </div>
            <div className="text-slate-300 leading-relaxed">
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-slate-700 rounded-lg overflow-hidden">
                  <thead className="bg-slate-800">
                    <tr>
                      <th className="text-left p-3 text-white border-b border-slate-700">Type de données</th>
                      <th className="text-left p-3 text-white border-b border-slate-700">Durée de conservation</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    <tr className="bg-slate-900/50">
                      <td className="p-3">Données de compte</td>
                      <td className="p-3">Durée d'utilisation du Service + 3 ans après dernière activité</td>
                    </tr>
                    <tr className="bg-slate-900/30">
                      <td className="p-3">Données de connexion (logs)</td>
                      <td className="p-3">12 mois</td>
                    </tr>
                    <tr className="bg-slate-900/50">
                      <td className="p-3">Historique des recherches</td>
                      <td className="p-3">6 mois (ou suppression à la demande)</td>
                    </tr>
                    <tr className="bg-slate-900/30">
                      <td className="p-3">Cookies non essentiels</td>
                      <td className="p-3">13 mois maximum (recommandation CNIL)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-slate-400 text-sm mt-4">Au-delà de ces durées, les données sont supprimées ou anonymisées.</p>
            </div>
          </section>

          {/* Article 9 - Droits des personnes */}
          <section className="bg-emerald-900/20 border border-emerald-700 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="text-emerald-400" size={20} />
              <h3 className="text-xl font-semibold text-white">Article 9 — Vos droits (RGPD)</h3>
            </div>
            <div className="text-slate-300 leading-relaxed space-y-4">
              <p>Conformément aux articles 15 à 22 du RGPD, vous disposez des droits suivants :</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-emerald-300 font-semibold mb-2">Droit d'accès</h4>
                  <p className="text-sm">Obtenir une copie de vos données personnelles</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-emerald-300 font-semibold mb-2">Droit de rectification</h4>
                  <p className="text-sm">Corriger vos données inexactes ou incomplètes</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-emerald-300 font-semibold mb-2">Droit à l'effacement</h4>
                  <p className="text-sm">« Droit à l'oubli », dans les limites légales</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-emerald-300 font-semibold mb-2">Droit à la limitation</h4>
                  <p className="text-sm">Limiter le traitement de vos données</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-emerald-300 font-semibold mb-2">Droit d'opposition</h4>
                  <p className="text-sm">Vous opposer au traitement de vos données</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-emerald-300 font-semibold mb-2">Droit à la portabilité</h4>
                  <p className="text-sm">Recevoir vos données dans un format structuré</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-emerald-300 font-semibold mb-2">Retrait du consentement</h4>
                  <p className="text-sm">Retirer votre consentement à tout moment</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-emerald-300 font-semibold mb-2">Directives post-mortem</h4>
                  <p className="text-sm">Définir le sort de vos données après décès</p>
                </div>
              </div>
              <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-4 mt-4">
                <p className="text-blue-200">
                  <strong>Pour exercer vos droits :</strong> Contactez <a href="mailto:dr.stepanian.tigran@gmail.com" className="text-blue-400 hover:underline">dr.stepanian.tigran@gmail.com</a>
                  <br />
                  Une preuve d'identité peut être demandée. L'Éditeur répond dans un délai d'un mois (prorogeable selon art. 12 RGPD).
                </p>
              </div>
            </div>
          </section>

          {/* Article 10 - CNIL */}
          <section className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Bell className="text-amber-400" size={20} />
              <h3 className="text-xl font-semibold text-white">Article 10 — Réclamation auprès de la CNIL</h3>
            </div>
            <div className="text-slate-300 leading-relaxed">
              <p className="mb-4">Toute personne estimant que ses droits ne sont pas respectés peut introduire une réclamation auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) :</p>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-white font-semibold">CNIL</p>
                <p className="text-slate-400">3 Place de Fontenoy, TSA 80715</p>
                <p className="text-slate-400">75334 Paris Cedex 07</p>
                <a 
                  href="https://www.cnil.fr" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-blue-400 hover:underline inline-flex items-center gap-1 mt-2"
                >
                  www.cnil.fr <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </section>

          {/* Article 11 - Sécurité */}
          <section className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="text-blue-400" size={20} />
              <h3 className="text-xl font-semibold text-white">Article 11 — Sécurité des données</h3>
            </div>
            <div className="text-slate-300 leading-relaxed space-y-4">
              <p>L'Éditeur met en œuvre des mesures techniques et organisationnelles appropriées (art. 32 RGPD) visant à préserver la confidentialité, l'intégrité et la disponibilité des données :</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Contrôle des accès et authentification sécurisée</li>
                <li>Chiffrement des données sensibles</li>
                <li>Journalisation des accès</li>
                <li>Sauvegardes régulières</li>
                <li>Sensibilisation des intervenants</li>
              </ul>
              <p className="text-slate-400 text-sm">
                En cas de violation de données susceptible d'engendrer un risque pour les droits et libertés, l'Éditeur procède aux notifications requises (art. 33 et 34 RGPD).
              </p>
            </div>
          </section>

          {/* Article 12 - Cookies */}
          <section className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Cookie className="text-amber-400" size={20} />
              <h3 className="text-xl font-semibold text-white">Article 12 — Cookies et traceurs</h3>
            </div>
            <div className="text-slate-300 leading-relaxed space-y-4">
              <p>Le Service peut recourir à des cookies et traceurs :</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="bg-emerald-900/30 border border-emerald-700/50 rounded-lg p-4">
                  <h4 className="text-emerald-300 font-semibold mb-2">Cookies strictement nécessaires</h4>
                  <p className="text-sm text-emerald-200">Déposés sans consentement (authentification, session)</p>
                </div>
                <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-4">
                  <h4 className="text-amber-300 font-semibold mb-2">Cookies non essentiels</h4>
                  <p className="text-sm text-amber-200">Déposés uniquement après recueil du consentement (mesure d'audience, etc.)</p>
                </div>
              </div>
              <p className="text-slate-400 text-sm mt-4">
                Conformément à l'article 82 de la Loi Informatique et Libertés et aux recommandations de la CNIL, vous pouvez gérer vos préférences via le bandeau cookies affiché lors de votre première visite.
              </p>
            </div>
          </section>

          {/* Article 13 - Modification */}
          <section className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <FileEdit className="text-slate-400" size={20} />
              <h3 className="text-xl font-semibold text-white">Article 13 — Modification de la politique</h3>
            </div>
            <p className="text-slate-300 leading-relaxed">
              La présente politique peut être modifiée pour tenir compte des évolutions légales, réglementaires ou techniques. La version applicable est celle en vigueur à la date de consultation. En cas de modification substantielle, l'Utilisateur en est informé par tout moyen approprié.
            </p>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-slate-800 text-center">
          <p className="text-xs text-slate-500">
            Document mis à jour en Décembre 2025<br />
            Service « Hippocrate MG » — Outil documentaire réservé aux professionnels de santé
          </p>
        </div>
      </div>
    </div>
  );
};
